import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import type { ManualBody } from "@arc/contracts";
import { manualApi } from "../../../api/client.js";

/**
 * Drives one manual print-out (ADR-031): `start(id)` fetches a FRESH body — never the reader's cache, whose presigned
 * media links may be minutes from expiring (panel High) — mounts {@link ManualPrintSheet} with it, prints once the
 * sheet reports every image settled, and unmounts after the print dialog closes. `printing` is the id in flight (the
 * button's "Preparing print…" state); `error` is an inline message for the host to render (no dialog dependency — the
 * drawer and the reader both render inline alerts).
 */
export function usePrintManual() {
  const { t } = useTranslation();
  const [printing, setPrinting] = useState<number | null>(null);
  const [body, setBody] = useState<ManualBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const start = useCallback(
    (manualId: number) => {
      if (printing !== null) return;
      setPrinting(manualId);
      setError(null);
      void manualApi
        .getManualBody({ params: { id: manualId } })
        .then((res) => {
          if (res.status !== 200) throw new Error(`GET /manuals/${String(manualId)}/body returned ${String(res.status)}`);
          setBody(res.body);
        })
        .catch(() => {
          setPrinting(null);
          setError(t("print.couldntPrepareThePrintOutPlease"));
        });
    },
    [printing],
  );
  const onReady = useCallback(() => window.print(), []);
  const onAfterPrint = useCallback(() => {
    setBody(null);
    setPrinting(null);
  }, []);
  return { printing, body, error, start, onReady, onAfterPrint };
}
